import React from "react";

/**
 * Catches any uncaught React render/lifecycle errors and shows a recoverable
 * screen instead of a blank white/beige page. Also surfaces the underlying
 * error details (toggle-revealed) so we can diagnose device-specific bugs
 * without needing a debugger attached.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null, showDetails: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[Hush] React error boundary caught:", error, info);
    this.setState({ info });
    const payload = {
      message: String((error && error.message) || error || "Unknown error"),
      stack: String((error && error.stack) || ""),
      component_stack: String((info && info.componentStack) || ""),
      ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
      url: typeof window !== "undefined" ? window.location.href : "",
      app_version: process.env.REACT_APP_VERSION || "1.0",
      platform: (() => {
        try {
          // eslint-disable-next-line global-require
          const { Capacitor } = require("@capacitor/core");
          return Capacitor.isNativePlatform() ? Capacitor.getPlatform() : "web";
        } catch (_) {
          return "web";
        }
      })(),
    };
    // Stash for Safari Web Inspector
    try { window.__hushLastError = { ...payload, time: new Date().toISOString() }; } catch (_) { /* noop */ }
    // Fire-and-forget POST to backend. Never let logging itself crash the UI.
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      if (base && typeof fetch === "function") {
        fetch(`${base}/api/errors`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => {});
      }
    } catch (_) { /* noop */ }
  }

  reset = () => {
    this.setState({ hasError: false, error: null, info: null, showDetails: false });
    window.location.reload();
  };

  toggleDetails = () => {
    this.setState((s) => ({ showDetails: !s.showDetails }));
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const err = this.state.error;
    const info = this.state.info;
    const message = (err && err.message) ? String(err.message) : String(err || "Unknown error");
    const stack = (err && err.stack) ? String(err.stack) : "";
    const compStack = (info && info.componentStack) ? String(info.componentStack) : "";
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center px-6 py-10 text-center"
        style={{ background: "#F9F6F0", color: "#2A3326" }}
        data-testid="error-boundary"
      >
        <h1 className="font-serif text-3xl mb-3">Welcome to Hush</h1>
        <p className="text-ink-500 max-w-sm mb-6 leading-relaxed">
          Something went wrong loading the app. Tap below to try again.
        </p>
        <button
          type="button"
          onClick={this.reset}
          className="btn-primary"
          data-testid="error-boundary-reload"
        >
          Reload Hush
        </button>
        <button
          type="button"
          onClick={this.toggleDetails}
          className="mt-6 text-[11px] text-ink-500 underline opacity-60"
          data-testid="error-boundary-details-toggle"
        >
          {this.state.showDetails ? "Hide" : "Show"} technical details
        </button>
        {this.state.showDetails && (
          <pre
            className="mt-4 text-[10px] text-left whitespace-pre-wrap break-words bg-bone-200 border border-bone-300 rounded-md p-3 max-w-md max-h-[40vh] overflow-auto"
            data-testid="error-boundary-details"
          >
            {`MESSAGE:\n${message}\n\nSTACK:\n${stack}\n\nCOMPONENT STACK:\n${compStack}\n\nUA:\n${typeof navigator !== "undefined" ? navigator.userAgent : ""}`}
          </pre>
        )}
      </div>
    );
  }
}
